import WorkforceIcon from 'assets/icons/workforce.svg?react';
import { ConnectedPageLayout } from 'components/layout/page-layout';
import { useCurrentUser } from 'hooks/auth';
import {
  useIsEnterpriseCompanyAccount,
  useIsVonageEnabled,
} from 'hooks/company';
import React, { useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  MessageToWorkforceModal,
  ProfileModals,
} from 'screens/company-user/workforce/modals';

// Tabs
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import { Alert, Box, Grid, Tab } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { RouterButton } from '@libs/ui/components/button';
import Typography, { OuterLink } from '@libs/ui/components/typography';

import ChatApp from './chat-app';
import SmsApp from './sms-app/SmsApp';

interface ChatProps {}

const PAGE_TITLE = 'Messenger';

const useStyles = makeStyles((theme) => ({
  container: {
    height: 'calc(100vh - 120px)',
    overflow: 'hidden',
  },
  '@global .typeFormBtn': {
    display: 'none',
  },
  alertMessage: {
    padding: 0,
  },
  alertIcon: {
    margin: 0,
  },
  alert: {
    background: '#FFCCBC',
  },
}));

const TAB_CHATS = 'chats';
const TAB_SMS = 'sms';

function getTabFromSearch(search: string) {
  const params = new URLSearchParams(search);
  return params.get('tab') || TAB_CHATS;
}

function setTabInSearch(search: string, tab: string) {
  const params = new URLSearchParams(search);
  params.set('tab', tab);
  return `?${params.toString()}`;
}

const Chat = (props: ChatProps) => {
  const classes = useStyles();
  const { isCompany } = useCurrentUser();
  const isEnterprise = useIsEnterpriseCompanyAccount();
  const isVonageEnabled = useIsVonageEnabled();
  const location = useLocation();
  const history = useHistory();

  // SMS tab should only be visible for company users with Vonage enabled
  const showSmsTab = isCompany && isVonageEnabled;

  const tab = useMemo(
    () => getTabFromSearch(location.search),
    [location.search],
  );

  // Redirect users from SMS tab to Messenger tab when SMS is not available
  React.useEffect(() => {
    if (!showSmsTab && tab === TAB_SMS) {
      history.replace({
        ...location,
        search: setTabInSearch(location.search, TAB_CHATS),
      });
    }
  }, [showSmsTab, tab, history, location]);

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: string) => {
    if (newValue !== tab) {
      history.replace({
        ...location,
        search: setTabInSearch(location.search, newValue),
      });
    }
  };

  return (
    <ConnectedPageLayout
      documentTitle={PAGE_TITLE}
      headerProps={{ accountProps: {} }}
      drawerProps={{}}
      classes={{ contentWrapper: classes.container }}
    >
      <Box py={2} pb={6}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid justifyContent="space-between" container>
              <Grid item>
                <Typography variant="h5">{PAGE_TITLE}</Typography>
              </Grid>
              {isEnterprise && isCompany && (
                <Grid item>
                  <RouterButton
                    to={{ state: { [ProfileModals.MessageToWorkforce]: true } }}
                    startIcon={<WorkforceIcon />}
                  >
                    message MY workforce
                  </RouterButton>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Alert
              classes={{
                root: classes.alert,
                message: classes.alertMessage,
                icon: classes.alertIcon,
              }}
              icon={<></>}
              color="error"
            >
              <Typography
                component="span"
                variant="body2"
                color="secondary.main"
              >
                To protect the quality of our community we have strict messaging
                guidelines.
              </Typography>{' '}
              <OuterLink
                variant="body2"
                color="info.main"
                target="_blank"
                href="https://www.notion.so/opentalent/Messaging-Guideline-21e9d00d7393469fb478c02bb816a7a0"
              >
                Learn more.
              </OuterLink>
            </Alert>
          </Grid>
        </Grid>
      </Box>
      {/* Tabs */}
      <TabContext value={tab}>
        <Box sx={{ borderColor: 'divider' }} mb={2}>
          <TabList
            onChange={handleTabChange}
            aria-label="chat tabs"
            variant="scrollable"
            allowScrollButtonsMobile
          >
            <Tab label="Messenger" value={TAB_CHATS} />
            {showSmsTab && <Tab label="SMS" value={TAB_SMS} />}
          </TabList>
        </Box>
        {/* Tab Panels */}
        {tab === TAB_CHATS && <ChatApp />}
        {showSmsTab && tab === TAB_SMS && <SmsApp />}
      </TabContext>
      <MessageToWorkforceModal />
    </ConnectedPageLayout>
  );
};

export default Chat;
